import Modal from "Common/Components/Modal";
import React from "react";
import { Loader2 } from "lucide-react";
import { useAppContext } from 'context/AppContext';
import { usePantoneColorModal } from './usePantoneColorModal';
import { FormikErrors } from 'formik';

type Props = { onHide: () => void; data: any; onSave: (result: any) => void };

export const AddPantoneColor: React.FC<Props> = ({ onHide, data, onSave }) => {
    const { user } = useAppContext();
    const { isSaving, validation } = usePantoneColorModal(onSave, data);

    const renderError = (error: string | FormikErrors<any> | string[] | FormikErrors<any>[] | undefined) => {
        if (typeof error === 'string') {
            return error;
        }
        return '';
    };

    return (
        <React.Fragment>
            <Modal.Header
                className="flex items-center justify-between p-4 border-b dark:border-zink-300/20"
                closeButtonClass="transition-all duration-200 ease-linear text-slate-400 hover:text-red-500"
            >
                <Modal.Title className="text-16">
                    {data?.id ? "Edit Pantone Color" : "Add Pantone Color"}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
                <form
                    action="#!"
                    onSubmit={(e) => {
                        e.preventDefault();
                        validation.handleSubmit();
                        return false;
                    }}
                >
                    <div className="grid grid-cols-1 gap-5 xl:grid-cols-12">
                        {/* Pantone Code */}
                        <div className="lg:col-span-6 xl:col-span-6">
                            <div className="flex items-center justify-between mb-2">
                                <label
                                    htmlFor="code"
                                    className="text-base font-medium"
                                >
                                    Pantone Code <span className="text-red-500">*</span>
                                </label>
                                {validation.touched.code && validation.errors.code ? (
                                    <span className="text-sm text-red-500">{renderError(validation.errors.code)}</span>
                                ) : null}
                            </div>
                            <input
                                type="text"
                                id="code"
                                className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                                placeholder="Enter pantone code"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.code || ""}
                            />
                        </div>

                        {/* Color Name */}
                        <div className="lg:col-span-6 xl:col-span-6">
                            <div className="flex items-center justify-between mb-2">
                                <label
                                    htmlFor="name"
                                    className="text-base font-medium"
                                >
                                    Color Name <span className="text-red-500">*</span>
                                </label>
                                {validation.touched.name && validation.errors.name ? (
                                    <span className="text-sm text-red-500">{renderError(validation.errors.name)}</span>
                                ) : null}
                            </div>
                            <input
                                type="text"
                                id="name"
                                className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                                placeholder="Enter color name"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.name || ""}
                            />
                        </div>

                        {/* Hex Color */}
                        <div className="lg:col-span-12 xl:col-span-12">
                            <div className="flex items-center justify-between mb-2">
                                <label
                                    htmlFor="hex_colour"
                                    className="text-base font-medium"
                                >
                                    Hex Color <span className="text-red-500">*</span>
                                </label>
                                {validation.touched.hex_colour && validation.errors.hex_colour ? (
                                    <span className="text-sm text-red-500">{renderError(validation.errors.hex_colour)}</span>
                                ) : null}
                            </div>
                            <div className="flex items-center gap-2">
                                <input
                                    type="text"
                                    id="hex_colour"
                                    className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                                    placeholder="#FFFFFF"
                                    onChange={validation.handleChange}
                                    onBlur={validation.handleBlur}
                                    value={validation.values.hex_colour || ""}
                                />
                                <div 
                                    className="w-10 h-10 rounded border border-slate-200 dark:border-zink-500"
                                    style={{ backgroundColor: validation.values.hex_colour || '#ffffff' }}
                                ></div>
                            </div>
                        </div>

                        {/* LAB Values */}
                        <div className="lg:col-span-12 xl:col-span-12">
                            <div className="grid grid-cols-3 gap-4">
                                {/* L Value */}
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <label
                                            htmlFor="l_value"
                                            className="text-base font-medium"
                                        >
                                            L Value <span className="text-red-500">*</span>
                                        </label>
                                        {validation.touched.l_value && validation.errors.l_value ? (
                                            <span className="text-sm text-red-500">{renderError(validation.errors.l_value)}</span>
                                        ) : null}
                                    </div>
                                    <input
                                        type="number"
                                        id="l_value"
                                        className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                                        placeholder="0-100"
                                        min="0"
                                        max="100"
                                        step="0.1"
                                        onChange={validation.handleChange}
                                        onBlur={validation.handleBlur}
                                        value={validation.values.l_value || ""}
                                    />
                                </div>

                                {/* A Value */}
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <label
                                            htmlFor="a_value"
                                            className="text-base font-medium"
                                        >
                                            A Value <span className="text-red-500">*</span>
                                        </label>
                                        {validation.touched.a_value && validation.errors.a_value ? (
                                            <span className="text-sm text-red-500">{renderError(validation.errors.a_value)}</span>
                                        ) : null}
                                    </div>
                                    <input
                                        type="number"
                                        id="a_value"
                                        className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                                        placeholder="-128 to 127"
                                        min="-128"
                                        max="127"
                                        step="0.1"
                                        onChange={validation.handleChange}
                                        onBlur={validation.handleBlur}
                                        value={validation.values.a_value || ""}
                                    />
                                </div>

                                {/* B Value */}
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <label
                                            htmlFor="b_value"
                                            className="text-base font-medium"
                                        >
                                            B Value <span className="text-red-500">*</span>
                                        </label>
                                        {validation.touched.b_value && validation.errors.b_value ? (
                                            <span className="text-sm text-red-500">{renderError(validation.errors.b_value)}</span>
                                        ) : null}
                                    </div>
                                    <input
                                        type="number"
                                        id="b_value"
                                        className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                                        placeholder="-128 to 127"
                                        min="-128"
                                        max="127"
                                        step="0.1"
                                        onChange={validation.handleChange}
                                        onBlur={validation.handleBlur}
                                        value={validation.values.b_value || ""}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* RGB Values */}
                        <div className="lg:col-span-12 xl:col-span-12">
                            <div className="grid grid-cols-3 gap-4">
                                {/* RGB R */}
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <label
                                            htmlFor="rgb_r"
                                            className="text-base font-medium"
                                        >
                                            RGB R <span className="text-red-500">*</span>
                                        </label>
                                        {validation.touched.rgb_r && validation.errors.rgb_r ? (
                                            <span className="text-sm text-red-500">{renderError(validation.errors.rgb_r)}</span>
                                        ) : null}
                                    </div>
                                    <input
                                        type="number"
                                        id="rgb_r"
                                        className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                                        placeholder="0-255"
                                        min="0"
                                        max="255"
                                        onChange={validation.handleChange}
                                        onBlur={validation.handleBlur}
                                        value={validation.values.rgb_r || ""}
                                    />
                                </div>

                                {/* RGB G */}
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <label
                                            htmlFor="rgb_g"
                                            className="text-base font-medium"
                                        >
                                            RGB G <span className="text-red-500">*</span>
                                        </label>
                                        {validation.touched.rgb_g && validation.errors.rgb_g ? (
                                            <span className="text-sm text-red-500">{renderError(validation.errors.rgb_g)}</span>
                                        ) : null}
                                    </div>
                                    <input
                                        type="number"
                                        id="rgb_g"
                                        className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                                        placeholder="0-255"
                                        min="0"
                                        max="255"
                                        onChange={validation.handleChange}
                                        onBlur={validation.handleBlur}
                                        value={validation.values.rgb_g || ""}
                                    />
                                </div>

                                {/* RGB B */}
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <label
                                            htmlFor="rgb_b"
                                            className="text-base font-medium"
                                        >
                                            RGB B <span className="text-red-500">*</span>
                                        </label>
                                        {validation.touched.rgb_b && validation.errors.rgb_b ? (
                                            <span className="text-sm text-red-500">{renderError(validation.errors.rgb_b)}</span>
                                        ) : null}
                                    </div>
                                    <input
                                        type="number"
                                        id="rgb_b"
                                        className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                                        placeholder="0-255"
                                        min="0"
                                        max="255"
                                        onChange={validation.handleChange}
                                        onBlur={validation.handleBlur}
                                        value={validation.values.rgb_b || ""}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end gap-2 mt-4">
                        <button
                            type="button"
                            className="text-red-500 bg-white btn hover:text-red-500 hover:bg-red-100 focus:text-red-500 focus:bg-red-100 active:text-red-500 active:bg-red-100 dark:bg-zink-600 dark:hover:bg-red-500/10 dark:focus:bg-red-500/10 dark:active:bg-red-500/10"
                            onClick={onHide}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSaving}
                            className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                        >
                            {isSaving ? (
                                <>
                                    <Loader2 className="inline-block size-4 animate-spin ltr:mr-2 rtl:ml-2" />
                                    {data?.id ? "Updating..." : "Adding..."}
                                </>
                            ) : (
                                <>{data?.id ? "Update Pantone Color" : "Add Pantone Color"}</>
                            )}
                        </button>
                    </div>
                </form>
            </Modal.Body>
        </React.Fragment>
    );
}; 